import type { GbTier } from "@/lib/pricing-utils";

/**
 * Pure maths for the Scraping Cost Calculator (/tools/scraping-cost-calculator).
 *
 * Kept free of React and of any pricing data: plan tiers are passed in by the caller so the
 * calculator always reads the same tier ladder as the residential pricing page.
 *
 * Model
 *   attemptsPerUsable   = 1 / successRate            (each attempt succeeds independently)
 *   trafficPerAttempt   = (1 - browserShare) * staticKb + browserShare * browserKb
 *   trafficPerUsable    = trafficPerAttempt * (1 + (attemptsPerUsable - 1) * failedWeight)
 *   proxyCost           = monthlyGb * pricePerGbOfTier
 *
 * A failed attempt costs `failedWeight` of a normal attempt: block pages and timeouts are often
 * smaller than a full page, but 100% is the conservative default.
 */

export const KB_PER_GB = 1_000_000; // decimal, matching how proxy bandwidth is billed

export interface CalculatorInputs {
  usablePagesPerMonth: number;
  /** Share of pages that need a real browser (JS rendering), 0–100. */
  browserSharePct: number;
  staticKbPerAttempt: number;
  browserKbPerAttempt: number;
  /** Share of attempts that return a usable result, 1–100. */
  successRatePct: number;
  /** Size of a failed attempt relative to a normal one, 0–100. */
  failedWeightPct: number;
}

export const DEFAULT_INPUTS: CalculatorInputs = {
  usablePagesPerMonth: 100_000,
  browserSharePct: 0,
  staticKbPerAttempt: 200,
  browserKbPerAttempt: 1_500,
  successRatePct: 85,
  failedWeightPct: 100,
};

export const INPUT_LIMITS = {
  usablePagesPerMonth: { min: 1_000, max: 100_000_000 },
  browserSharePct: { min: 0, max: 100 },
  staticKbPerAttempt: { min: 1, max: 20_000 },
  browserKbPerAttempt: { min: 1, max: 50_000 },
  successRatePct: { min: 1, max: 100 },
  failedWeightPct: { min: 0, max: 100 },
} as const;

export interface PlanRecommendation {
  tier: GbTier;
  /** Share of the plan's data the estimate uses, 0–1. */
  utilisation: number;
}

export interface CalculatorResult {
  attemptsPerUsable: number;
  totalAttempts: number;
  trafficPerAttemptKb: number;
  trafficPerUsableKb: number;
  monthlyGb: number;
  /** Tier that covers monthlyGb, or null when the estimate exceeds the largest tier. */
  plan: PlanRecommendation | null;
  /** True when the estimate is above the largest public tier. */
  exceedsPublicTiers: boolean;
  /** Per-GB rate used for the usage cost (the recommended tier's, else the largest tier's). */
  pricePerGb: number;
  proxyUsageCost: number;
  costPer1kUsable: number;
  /** What you'd pay if you buy the whole plan and only use the estimate (rollover aside). */
  planPrice: number | null;
}

export function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}

export function sanitizeInputs(raw: CalculatorInputs): CalculatorInputs {
  const l = INPUT_LIMITS;
  return {
    usablePagesPerMonth: clamp(
      Math.round(raw.usablePagesPerMonth),
      l.usablePagesPerMonth.min,
      l.usablePagesPerMonth.max
    ),
    browserSharePct: clamp(raw.browserSharePct, l.browserSharePct.min, l.browserSharePct.max),
    staticKbPerAttempt: clamp(
      raw.staticKbPerAttempt,
      l.staticKbPerAttempt.min,
      l.staticKbPerAttempt.max
    ),
    browserKbPerAttempt: clamp(
      raw.browserKbPerAttempt,
      l.browserKbPerAttempt.min,
      l.browserKbPerAttempt.max
    ),
    successRatePct: clamp(raw.successRatePct, l.successRatePct.min, l.successRatePct.max),
    failedWeightPct: clamp(raw.failedWeightPct, l.failedWeightPct.min, l.failedWeightPct.max),
  };
}

/** Smallest tier whose monthly data covers `gb`, or null if even the largest is too small. */
export function pickTier(tiers: GbTier[], gb: number): GbTier | null {
  const sorted = [...tiers].sort((a, b) => a.gb - b.gb);
  return sorted.find((t) => t.gb >= gb) ?? null;
}

export function calculate(rawInputs: CalculatorInputs, tiers: GbTier[]): CalculatorResult {
  const inputs = sanitizeInputs(rawInputs);
  const success = inputs.successRatePct / 100;
  const browserShare = inputs.browserSharePct / 100;
  const failedWeight = inputs.failedWeightPct / 100;

  const attemptsPerUsable = 1 / success;
  const totalAttempts = inputs.usablePagesPerMonth * attemptsPerUsable;

  const trafficPerAttemptKb =
    (1 - browserShare) * inputs.staticKbPerAttempt + browserShare * inputs.browserKbPerAttempt;
  const trafficPerUsableKb = trafficPerAttemptKb * (1 + (attemptsPerUsable - 1) * failedWeight);

  const monthlyGb = (trafficPerUsableKb * inputs.usablePagesPerMonth) / KB_PER_GB;

  const sorted = [...tiers].sort((a, b) => a.gb - b.gb);
  const largest = sorted[sorted.length - 1];
  const tier = pickTier(sorted, monthlyGb);
  const pricePerGb = (tier ?? largest)?.monthlyPPG ?? 0;

  const proxyUsageCost = monthlyGb * pricePerGb;
  const costPer1kUsable = proxyUsageCost / (inputs.usablePagesPerMonth / 1000);

  return {
    attemptsPerUsable,
    totalAttempts,
    trafficPerAttemptKb,
    trafficPerUsableKb,
    monthlyGb,
    plan: tier ? { tier, utilisation: Math.min(1, monthlyGb / tier.gb) } : null,
    exceedsPublicTiers: tier === null,
    pricePerGb,
    proxyUsageCost,
    costPer1kUsable,
    planPrice: tier ? tier.totalPrice : null,
  };
}

// ---------------------------------------------------------------------------------------------
// Shareable URL: every input maps to one short query param. Defaults are omitted to keep links short.
// ---------------------------------------------------------------------------------------------

const PARAM_KEYS: Record<keyof CalculatorInputs, string> = {
  usablePagesPerMonth: "pages",
  browserSharePct: "js",
  staticKbPerAttempt: "skb",
  browserKbPerAttempt: "bkb",
  successRatePct: "ok",
  failedWeightPct: "fw",
};

export function inputsToSearchParams(inputs: CalculatorInputs): URLSearchParams {
  const params = new URLSearchParams();
  (Object.keys(PARAM_KEYS) as (keyof CalculatorInputs)[]).forEach((key) => {
    const value = inputs[key];
    if (value === null || value === DEFAULT_INPUTS[key]) return;
    params.set(PARAM_KEYS[key], String(value));
  });
  return params;
}

export function inputsFromSearchParams(params: URLSearchParams): CalculatorInputs {
  const next: CalculatorInputs = { ...DEFAULT_INPUTS };
  (Object.keys(PARAM_KEYS) as (keyof CalculatorInputs)[]).forEach((key) => {
    const raw = params.get(PARAM_KEYS[key]);
    if (raw === null || raw.trim() === "") return;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return;
    (next as unknown as Record<string, number | null>)[key] = parsed;
  });
  return sanitizeInputs(next);
}

export function formatUsd(value: number): string {
  if (!Number.isFinite(value)) return "$0";
  if (value >= 1000) return `$${Math.round(value).toLocaleString("en-US")}`;
  if (value >= 0.1) return `$${value.toFixed(2)}`;
  return `$${value.toFixed(3)}`;
}

export function formatGb(gb: number): string {
  if (gb >= 1000) return `${(gb / 1000).toFixed(2)} TB`;
  if (gb >= 100) return `${Math.round(gb)} GB`;
  return `${gb.toFixed(2)} GB`;
}
